import React, { useState } from "react";
import { Button, Modal, useSnackBar } from "@truevault/aquilo";
import { OrganizationPublicId } from "../../../common/service/server/Types";
import { McpTokenDto } from "../../../common/service/server/dto/McpTokenDto";
import { useMcpTokenActions, useOrganizationMcpTokens } from "../../hooks/useOrganization";

/** Stands in for the secret in the setup snippets, which are shown long after it's unrecoverable. */
const TOKEN_PLACEHOLDER = "pol_mcp_YOUR_TOKEN";

/**
 * The MCP server is deployed separately from Polaris, on its own hostname per environment (see
 * `services/polaris-mcp/README.md`), so the snippets have to follow whichever Polaris the user is
 * looking at. Prod's DNS is not cut over yet, but the hostname it will answer on is already fixed
 * by `services/polaris-mcp/infrastructure/main.tf`. Anything else — localhost, a review app — gets
 * the local dev server, which is what a developer running Polaris on their machine has.
 */
const mcpServerUrl = (): string => {
  const { hostname } = window.location;

  if (hostname.endsWith("truevaultstaging.com")) {
    return "https://mcp.truevaultstaging.com/mcp";
  }

  if (hostname.endsWith("truevault.com")) {
    return "https://mcp.truevault.com/mcp";
  }

  return "http://localhost:5003/mcp";
};

const claudeCodeCommand = (serverUrl: string, token: string): string =>
  `claude mcp add --transport http --scope project truevault-polaris ${serverUrl} --header "Authorization: Bearer ${token}"`;

const genericMcpConfig = (serverUrl: string, token: string): string =>
  JSON.stringify(
    {
      mcpServers: {
        "truevault-polaris": {
          serverUrl,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      },
    },
    null,
    2,
  );

/** Aquilo's `size` presets pin a fixed width, which clips labels this long. */
const ACTION_BUTTON_SIZE = "h-8.5 py-[9px] text-sm leading-[20px] whitespace-nowrap";

/** Labels a token we can't display, and stands in for the secret in the setup instructions. */
const maskToken = (token: McpTokenDto): string => `${token.tokenPrefix}…`;

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

/** "in 12 days" reads better than a bare date when the whole point is that it runs out. */
const describeExpiry = (token: McpTokenDto): string => {
  if (token.expired) {
    return `Expired ${formatDate(token.expiresAt)}`;
  }

  const days = Math.ceil(
    (new Date(token.expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000),
  );
  const relative = days <= 1 ? "today" : `in ${days} days`;
  return `Expires ${formatDate(token.expiresAt)} (${relative})`;
};

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.3333 6H7.33333C6.59695 6 6 6.59695 6 7.33333V13.3333C6 14.0697 6.59695 14.6667 7.33333 14.6667H13.3333C14.0697 14.6667 14.6667 14.0697 14.6667 13.3333V7.33333C14.6667 6.59695 14.0697 6 13.3333 6Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.33333 10H2.66667C2.31305 10 1.97391 9.85953 1.72386 9.60949C1.47381 9.35944 1.33333 9.0203 1.33333 8.66667V2.66667C1.33333 2.31305 1.47381 1.97391 1.72386 1.72386C1.97391 1.47381 2.31305 1.33333 2.66667 1.33333H8.66667C9.0203 1.33333 9.35944 1.47381 9.60949 1.72386C9.85953 1.97391 10 2.31305 10 2.66667V3.33333"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M13.3333 4L6 11.3333L2.66667 8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CodeBlock({
  code,
  language,
  wrap = false,
}: {
  code: string;
  language?: string;
  wrap?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text: ", err);
    }
  };

  return (
    <div className="group relative w-full rounded-[12px] border border-solid border-neutral-200 bg-[#F5F4F9]">
      {language && (
        <div className="pl-[28px] pt-[18px] font-mono text-[12px] leading-[18px] text-[#918CA1]">
          {language}
        </div>
      )}
      <pre
        className={`m-0 overflow-x-auto ${
          language ? "pt-[8px]" : "pt-[28px]"
        } pb-[28px] pl-[28px] pr-[68px] font-mono text-[13px] leading-[22px] text-[#1A1726] ${
          wrap ? "whitespace-pre-wrap break-all" : "whitespace-pre"
        }`}
      >
        {code}
      </pre>
      <button
        type="button"
        onClick={handleCopy}
        aria-label="Copy to clipboard"
        className="absolute right-[18px] top-[18px] flex h-[36px] w-[36px] items-center justify-center rounded-[10px] text-[#918CA1] transition-colors hover:bg-black/5 hover:text-neutral-900"
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
    </div>
  );
}

/**
 * How to wire a token into a client, shown once for the card rather than per token: the steps are
 * identical whichever token you use, and we couldn't put a real one in them anyway — Polaris keeps
 * only a hash, so the snippets carry a placeholder for the user to swap out.
 */
function SetupInstructions() {
  const serverUrl = mcpServerUrl();

  return (
    <div className="mt-[24px] w-full border-t border-solid border-neutral-200 pt-[20px]">
      <p className="m-0 text-[14px] font-medium leading-[21px] text-neutral-900">
        Connecting an MCP client
      </p>
      <p className="mb-0 mt-[2px] max-w-[590px] text-[13px] leading-[20px] text-neutral-700">
        Swap <code className="font-mono">{TOKEN_PLACEHOLDER}</code> for the token you copied when
        you generated it.
      </p>

      <div className="mt-[16px]">
        <p className="m-0 text-[13px] font-medium leading-[20px] text-neutral-900">Claude Code</p>
        <p className="mb-0 mt-[2px] max-w-[590px] text-[13px] leading-[20px] text-neutral-700">
          Run this in your terminal to connect Claude Code to this organization.
        </p>
        <div className="mt-[10px]">
          <CodeBlock code={claudeCodeCommand(serverUrl, TOKEN_PLACEHOLDER)} wrap />
        </div>
      </div>

      <div className="mt-[20px]">
        <p className="m-0 text-[13px] font-medium leading-[20px] text-neutral-900">
          Generic MCP config
        </p>
        <p className="mb-0 mt-[2px] max-w-[590px] text-[13px] leading-[20px] text-neutral-700">
          For MCP clients that read a JSON config file (Cursor, Windsurf, etc.), add this entry to{" "}
          <code className="font-mono">.agents/mcp_config.json</code>.
        </p>
        <div className="mt-[10px]">
          <CodeBlock language="json" code={genericMcpConfig(serverUrl, TOKEN_PLACEHOLDER)} />
        </div>
      </div>
    </div>
  );
}

/**
 * The one and only chance to copy a token. Polaris keeps just a hash, so this panel stays up until
 * the user dismisses it and cannot be brought back.
 */
function NewTokenPanel({ token, onDismiss }: { token: string; onDismiss: () => void }) {
  return (
    <div className="mt-[24px] w-full rounded-[12px] border border-solid border-amber-300 bg-amber-50 p-[24px]">
      <div className="flex items-start justify-between gap-[16px]">
        <div>
          <p className="m-0 text-[14px] font-medium leading-[21px] text-neutral-900">
            Copy your token now
          </p>
          <p className="mb-0 mt-[2px] max-w-[590px] text-[13px] leading-[20px] text-neutral-700">
            This is the only time it will be shown. TrueVault stores only a hash of it, so if you
            lose it you will need to revoke this token and generate a new one.
          </p>
        </div>
        <div className="shrink-0">
          <Button color="tertiary" customSizeClassname={ACTION_BUTTON_SIZE} onClick={onDismiss}>
            Done
          </Button>
        </div>
      </div>

      <div className="mt-[16px]">
        <CodeBlock code={token} wrap />
      </div>

      <p className="mb-0 mt-[12px] max-w-[590px] text-[13px] leading-[20px] text-neutral-700">
        Setup instructions for Claude Code and other MCP clients are below.
      </p>
    </div>
  );
}

function TokenRow({
  token,
  onExtend,
  onRevoke,
  busy,
}: {
  token: McpTokenDto;
  onExtend: () => void;
  onRevoke: () => void;
  busy: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-[16px] border-b border-solid border-neutral-200 py-[14px] last:border-b-0">
      <div>
        <p className="m-0 font-mono text-[13px] leading-[20px] text-neutral-900">
          {maskToken(token)}
        </p>
        <p
          className={`mb-0 mt-[2px] text-[12px] leading-[18px] ${
            token.expired ? "text-red-600" : "text-neutral-700"
          }`}
        >
          Created {formatDate(token.createdAt)} &middot; {describeExpiry(token)}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-[8px]">
        <Button
          color="tertiary"
          customSizeClassname={ACTION_BUTTON_SIZE}
          onClick={onExtend}
          disabled={busy}
        >
          Extend 30 days
        </Button>
        <Button
          color="warning-secondary"
          customSizeClassname={ACTION_BUTTON_SIZE}
          onClick={onRevoke}
          disabled={busy}
        >
          Revoke
        </Button>
      </div>
    </div>
  );
}

export function McpAccessCard({ organizationId }: { organizationId: OrganizationPublicId }) {
  const enqueueSnackbar = useSnackBar();
  const [tokens, request] = useOrganizationMcpTokens(organizationId);
  const { createToken, extendToken, revokeToken } = useMcpTokenActions(organizationId);

  // Held in component state rather than refetched: the server will never hand it over again.
  const [newToken, setNewToken] = useState<string | undefined>(undefined);
  const [pendingRevoke, setPendingRevoke] = useState<McpTokenDto | undefined>(undefined);

  const busy = createToken.isLoading || extendToken.isLoading || revokeToken.isLoading;

  const handleGenerate = async () => {
    try {
      const created = await createToken.mutateAsync();
      setNewToken(created.token);
    } catch (err) {
      enqueueSnackbar("We couldn't generate an access token. Please try again.", {
        variant: "error",
      });
    }
  };

  const handleExtend = async (token: McpTokenDto) => {
    try {
      const extended = await extendToken.mutateAsync(token.id);
      enqueueSnackbar(`Token ${maskToken(token)} now expires ${formatDate(extended.expiresAt)}.`, {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar("We couldn't extend that token. Please try again.", { variant: "error" });
    }
  };

  const handleRevoke = async (token: McpTokenDto) => {
    setPendingRevoke(undefined);

    try {
      await revokeToken.mutateAsync(token.id);
      enqueueSnackbar(`Token ${maskToken(token)} was revoked.`, { variant: "success" });
    } catch (err) {
      enqueueSnackbar("We couldn't revoke that token. Please try again.", { variant: "error" });
    }
  };

  return (
    <div className="tw-boundary">
      <div className="mt-[24px] w-full rounded-[24px] border border-solid border-neutral-300 bg-white p-[36px] shadow-[0px_2px_12px_0px_rgba(0,0,0,0.12)]">
        <div className="flex flex-col items-start">
          <div className="flex w-full items-start justify-between gap-[16px]">
            <div>
              <h2 className="m-0 text-[18px] font-medium leading-[28px] text-neutral-900">
                MCP access tokens
              </h2>
              <p className="mb-0 mt-[6px] max-w-[590px] text-[14px] leading-[21px] text-neutral-700">
                Give a token to an MCP server so an AI assistant can read this organization&apos;s
                vendors and data map on your behalf. A token is read-only, sees exactly what you can
                see, expires after 30 days, and stops working if you leave the organization. Treat
                it like a password.
              </p>
            </div>
            <div className="shrink-0">
              <Button
                color="primary"
                customSizeClassname={ACTION_BUTTON_SIZE}
                onClick={handleGenerate}
                disabled={busy}
              >
                Generate token
              </Button>
            </div>
          </div>

          {newToken && <NewTokenPanel token={newToken} onDismiss={() => setNewToken(undefined)} />}

          <div className="mt-[24px] w-full border-t border-solid border-neutral-200 pt-[8px]">
            {request.running && !tokens && (
              <p className="my-[14px] text-[13px] leading-[20px] text-neutral-700">
                Loading tokens&hellip;
              </p>
            )}
            {tokens?.length === 0 && (
              <p className="my-[14px] text-[13px] leading-[20px] text-neutral-700">
                You don&apos;t have any MCP access tokens yet.
              </p>
            )}
            {tokens?.map((token) => (
              <TokenRow
                key={token.id}
                token={token}
                busy={busy}
                onExtend={() => handleExtend(token)}
                onRevoke={() => setPendingRevoke(token)}
              />
            ))}
          </div>

          {tokens && tokens.length > 0 && <SetupInstructions />}
        </div>
      </div>

      {pendingRevoke && (
        <Modal
          title={`Revoke ${pendingRevoke.tokenPrefix}…?`}
          body="Any MCP client using this token will stop working immediately. This cannot be undone."
          alignText="left"
          alignButton="right"
          firstButton="Cancel"
          onFirstButtonClick={() => setPendingRevoke(undefined)}
          secondButton="Revoke token"
          secondButtonColor="warning"
          onSecondButtonClick={() => void handleRevoke(pendingRevoke)}
          showOverlay
          onClose={() => setPendingRevoke(undefined)}
        />
      )}
    </div>
  );
}
